import { z } from "zod";

/**
 * The organization's tool-execution logging policy, as the Coordinator reports it
 * on an organization read.
 *
 * `ALLOWED` permits recording rather than compelling it — a project that declines
 * still records nothing. `DISABLED` stops it everywhere.
 */
export type LoggingDataRetention = "ALLOWED" | "DISABLED";

export type ModelTrainingAllowed = "ENABLED" | "DISABLED";

/**
 * Parsed rather than asserted. Every field here arrived with the policy feature,
 * so a deployment whose Coordinator predates it answers the same endpoint with
 * the same status and none of these keys. Trusting the type there puts
 * `undefined` through the form: the window renders blank, the ceiling reaches
 * the reader as "Up to undefined days", and the edit gate reads falsy, so the
 * controls disable themselves and claim the caller lacks permission. Failing at
 * the boundary says what is actually wrong instead.
 */
export const organizationLoggingPolicySchema = z.object({
  logging_data_retention: z.enum(["ALLOWED", "DISABLED"]),
  default_log_retention_days: z.number().int(),
  /**
   * The longest window this organization may set. Reported by the API rather than
   * known here: it is deployment configuration today and could be plan-derived
   * later, so a copy in the browser would be a second source that can disagree.
   */
  max_log_retention_days: z.number().int(),
  /**
   * Whether the caller may change the policy. Reported by the API rather than
   * derived from the org-metadata permission, which is allow-all while RBAC is
   * off — the write requires the organization's owner there, so the metadata
   * permission would offer a control the write refuses.
   */
  can_edit_logging: z.boolean(),
  model_training_allowed: z.enum(["ENABLED", "DISABLED"]),
  can_edit_privacy: z.boolean(),
});

export type OrganizationLoggingPolicy = z.infer<
  typeof organizationLoggingPolicySchema
>;

/**
 * What the write answers with — the policy it stored, and not one field more.
 * `can_edit_logging` is absent because the write does not change who may edit,
 * so this is deliberately narrower than the read and must be merged into a
 * cached policy rather than replacing one.
 */
export const organizationLoggingWriteSchema =
  organizationLoggingPolicySchema.omit({
    can_edit_logging: true,
    can_edit_privacy: true,
    model_training_allowed: true,
  });

export type OrganizationLoggingWriteResult = z.infer<
  typeof organizationLoggingWriteSchema
>;

export const organizationPrivacyWriteSchema =
  organizationLoggingPolicySchema.pick({ model_training_allowed: true });

export type OrganizationPrivacyWriteResult = z.infer<
  typeof organizationPrivacyWriteSchema
>;

/** Message for a response that parsed as JSON but is missing the policy. */
export const POLICY_UNAVAILABLE =
  "This deployment did not report a logging policy. Its Coordinator may predate the feature.";

/**
 * The window a customer may ask for. Both ends are real refusals from the API:
 * zero already means "keep forever" in storage, and a window longer than the
 * maximum is refused rather than quietly shortened.
 *
 * The ceiling is a parameter rather than a constant because the API reports it —
 * hardcoding it would mean guessing, and a guess that drifts refuses windows the
 * server would have accepted. The floor is a constant for the opposite reason:
 * one day is the smallest window the storage can express, not a policy the
 * deployment sets, so there is nothing for the API to report and nothing to
 * drift from.
 */
const MIN_RETENTION_DAYS = 1;

/**
 * What the box hands the form: null for an empty box, and never undefined.
 *
 * react-hook-form answers a read of an undefined field with that field's own
 * default, so undefined would put the stored window back in the box on the
 * keystroke that cleared it and leave no way to type a replacement. Null is
 * returned as itself.
 */
export const retentionDaysFromInput = (raw: string): number | null =>
  raw === "" ? null : Number(raw);

export const makeLoggingPolicyFormSchema = (
  maxRetentionDays: number,
  storedRetentionDays: number
) =>
  z.object({
    recordExecutions: z.boolean(),
    // Not coerced: the field hands over what `retentionDaysFromInput` returns.
    // Coercing here would turn an emptied box into 0 and report it as "at least
    // one day", a rule the caller has not broken, and it would make the schema's
    // input and output types differ, which react-hook-form's resolver will not
    // accept.
    retentionDays: z
      .number({ error: "Enter a number of days." })
      .int("Enter a whole number of days.")
      .min(MIN_RETENTION_DAYS, "Keep executions for at least one day.")
      .refine(
        // Lowering the platform maximum leaves organizations above it. Rejecting
        // the value they already have would make the form unusable for them —
        // including for turning recording off, which is the one change such an
        // organization is most likely to need. Only a new value must fit.
        (days) => days <= maxRetentionDays || days === storedRetentionDays,
        `Keep executions for at most ${maxRetentionDays} days.`
      ),
  });

/**
 * Whether saving this window destroys history that is currently kept.
 *
 * Shortening the window is retroactive: the retention sweep re-evaluates every
 * stored execution against the current setting, so history that predates the new
 * window is deleted rather than merely stopping the clock on new runs. Raising it
 * or leaving it alone destroys nothing, and turning recording off destroys nothing
 * either — the sweep reads the window, not whether recording is on.
 *
 * An organization sitting above a lowered platform maximum reaches this too: for
 * them every accepted window is a reduction, which is exactly when a warning is
 * worth the most.
 */
export const lowersRetention = (
  nextRetentionDays: number,
  storedRetentionDays: number
): boolean => nextRetentionDays < storedRetentionDays;

/**
 * Whether saving this turns recording off across the organization.
 *
 * Unlike shortening the window, this destroys nothing: the sweep reads the
 * window and never the recording flag, so history already recorded serves out
 * its window and expires on the usual schedule. What it does do is stop every
 * project recording at once, which is worth stating before it happens.
 */
export const stopsRecording = (
  nextRecordExecutions: boolean,
  storedRetention: LoggingDataRetention
): boolean => !nextRecordExecutions && storedRetention === "ALLOWED";

export const trainingConsentOf = (
  allowTraining: boolean
): ModelTrainingAllowed => (allowTraining ? "ENABLED" : "DISABLED");

export const makeOrganizationLoggingFormSchema = (
  maxRetentionDays: number,
  storedRetentionDays: number
) =>
  makeLoggingPolicyFormSchema(maxRetentionDays, storedRetentionDays).extend({
    allowTraining: z.boolean(),
  });

export type OrganizationLoggingFormValues = z.infer<
  ReturnType<typeof makeOrganizationLoggingFormSchema>
>;

/**
 * What became of one half of a save: a write that was not needed, one the API
 * stored, or one it refused.
 */
export type SaveAttemptState = "skipped" | "saved" | "refused";

export type SaveAttempt = {
  logging: SaveAttemptState;
  privacy: SaveAttemptState;
};

export type SaveOutcome = {
  clearLogging: boolean;
  clearPrivacy: boolean;
  error: string | null;
  saved: boolean;
};

/**
 * What the form does with a save that reached both halves independently.
 *
 * The two halves are separate writes against separate endpoints, so one can be
 * stored while the other is refused. A refused half stays unsaved work — its
 * value and its dirty flag are what let the caller press Save again — while the
 * stored half is adopted, and the caller is told which half is still pending by
 * name rather than being left to compare the form against the page.
 */
export const saveOutcomeOf = ({
  logging,
  privacy,
}: SaveAttempt): SaveOutcome => {
  const loggingRefused = logging === "refused";
  const privacyRefused = privacy === "refused";
  const anyStored = logging === "saved" || privacy === "saved";

  let error: string | null = null;
  if (loggingRefused && privacyRefused) {
    error = "Nothing was saved. Your changes are still here.";
  } else if (loggingRefused) {
    error =
      "Recording and history were not saved. Your changes are still here.";
  } else if (privacyRefused) {
    error = "Model training was not saved. Your change is still here.";
  }

  return {
    clearLogging: !loggingRefused,
    clearPrivacy: !privacyRefused,
    error,
    saved: anyStored && error === null,
  };
};
